import { useEffect, useState } from "react";
import { ExchangeHistoryDetails } from "../../models/response/exchangeHistoryListResponse";
import { UserHistoryListRequest } from "../../../../models/Request/userHistoryListRequest";
import { UsersExchangeTransactionHistory } from "../../services/transactions.service";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { TransactionStatusTypes } from "../../../../models/constants/transactionStatusTypes";
import { ExchangeCategoryType } from "../../../../models/enums/exchangeCategoryType";
import { ExchangeMatchType } from "../../../../models/constants/exchangeMatchType";
import { EXCHANGE_STATUS_STYLE } from "../../models/constants/statusStyles";
import localization from "../../../../config/localization";
import TransactionFilters from "./TransactionFilters";
import InfiniteScroll from "../../../../components/ui/InfiniteScroll";
import TransactionCard from "../../../../components/ui/TransactionCard";
import common from "../../../../utils/common";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";

export default function Exchange() {
  const dispatch = useAppDispatch();
  type Filters = {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };

  const Default_Filters: Filters = {
    searchText: null,
    fromDate: null,
    toDate: null,
    statuses: [] as string[],
  };

  const [loading, setLoading] = useState(true);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [exchangeData, setExchangeData] = useState<ExchangeHistoryDetails[]>(
    [],
  );
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFilters] = useState<Filters>(Default_Filters);

  const matchStyles: Record<string, string> = {
    Back: "text-[#63a7d9]",
    Lay: "text-[#ff7297]",
    Yes: "text-[#63a7d9]",
    No: "text-[#ff7297]",
  };

  function createExchangeHistoryRequest(
    pageNo: number,
    filters: {
      searchText: string | null;
      fromDate: string | null;
      toDate: string | null;
      statuses: string[];
    },
  ): UserHistoryListRequest {
    return {
      pageNo: pageNo,
      pageSize: 20,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      transactionId: filters.searchText?.trim() || null,
      status: filters.statuses.length ? filters.statuses.join(",") : null,
      isCallFromMobileDevice: false,
    };
  }

  const handleFiltersChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const fetchData = async (isReset = false, appliedFilters = filters) => {
    if (isLoadingMore && !isReset) {
      return;
    }

    if (isReset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setIsLoadingMore(true);
    }

    try {
      const currentPageNo = isReset ? 0 : pageNo;
      const request = createExchangeHistoryRequest(currentPageNo, appliedFilters);
      const newData = await UsersExchangeTransactionHistory(request);

      if (newData) {
        setExchangeData((prev) => {
          if (isReset || !prev) {
            return newData;
          }
          return [...prev, ...newData];
        });
      }

      if (!newData || newData.length < 20) {
        setHasMore(false);
      }
      setPageNo((prev) => (isReset ? 1 : prev + 1));
    } finally {
      if (isReset) {
        setLoading(false);
        dispatch(changeLoaderState(false));
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const resetPagination = () => {
    setExchangeData([]);
    setPageNo(0);
    setHasMore(true);

    // Reset UI state
    setOpenRowIds([]);
  };

  const handleApply = async (appliedFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => {
    setFilters(appliedFilters);
    resetPagination();

    await fetchData(true, appliedFilters);
  };

  const handleCardToggle = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) => {
      if (isOpen) {
        if (!prev.includes(itemId)) {
          return [...prev, itemId];
        }
        return prev;
      } else {
        return prev.filter((id) => id !== itemId);
      }
    });
  };

  useEffect(() => {
    const init = async () => {
      await fetchData(true);
    };

    init();
  }, []);

  const calculateLiability = (item: ExchangeHistoryDetails) => {
    let liability = item.stake;

    if (
      item.matchtype !== null &&
      (item.matchtype === ExchangeMatchType.lay ||
        item.matchtype === ExchangeMatchType.no)
    ) {
      if (
        item.categorytype === ExchangeCategoryType.fancyBat ||
        item.categorytype === ExchangeCategoryType.bookmaker
      ) {
        if (
          item.categorytype === ExchangeCategoryType.bookmaker &&
          item.eventtype === 1
        ) {
          liability = (item.actualodds - 1) * item.stake;
        } else {
          liability =
            (item.actualodds === null
              ? item.matchoddsreq
              : item.actualodds / 100) * item.stake;
        }
      } else {
        liability = (item.actualodds - 1) * item.stake;
      }
    }
    return liability;
  };

  const calculateProfit = (item: ExchangeHistoryDetails): string => {
    let profit: string;

    if (item.profitloss > 0 && item.status === TransactionStatusTypes.WIN) {
      profit = `+${item.profitloss.toFixed(2)}`;
    } else if (
      item.profitloss < 0 &&
      item.status === TransactionStatusTypes.LOST
    ) {
      profit = `${item && item.liability ? item.liability.toFixed(2) : item.profitloss.toFixed(2)}`;
    } else if (item.status === TransactionStatusTypes.OPEN) {
      profit = "---";
    } else {
      profit = "0.0";
    }

    return profit;
  };

  return (
    <div>
      <div>
        <TransactionFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={[
            localization.open,
            localization.win,
            localization.lost,
            localization.voided,
            localization.cancel,
            localization.commission,
          ]}
          onApply={handleApply}
        />
      </div>

      <InfiniteScroll
        onLoadMore={() => fetchData()}
        hasMore={hasMore}
        isLoading={loading || isLoadingMore}
      >
        {!loading && exchangeData?.length === 0 && (
          <div className="text-xl flex justify-center mt-1">
            {localization.no_exchange_history}
          </div>
        )}
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {exchangeData?.length > 0 &&
            exchangeData?.map((item) => {
                return (
                  <div key={item.id}>
                    <TransactionCard
                      onToggleChange={(isOpen) =>
                        handleCardToggle(item.id, isOpen)
                      }
                    >
                      <div className="grid grid-rows-2 gap-2 text-sm text-[#A9A9A9]">
                        <div>
                          <span>
                            {item.transactiondate
                              ? formatUtcToLocalDateTime(item.transactiondate)
                              : "-"}
                          </span>

                          <span
                            className={`float-right font-medium p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm xl:landscape:text-sm rounded-sm ${EXCHANGE_STATUS_STYLE[item.status]
                              }`}
                          >
                            {localization.getString(item.status.toLowerCase())}
                          </span>
                        </div>

                        <div className=" text-sm text-white">
                          <span className="font-semibold md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-base xl:landscape:text-base">
                            <img
                              src={`/icons/Exchange/EventType/${item.eventtype}.svg`}
                              alt="icon"
                              className="size-5 inline mr-2"
                            />
                            {item.selectionname}
                          </span>
                          <span className="float-right font-medium">
                            {calculateProfit(item) + "\u20B9"}
                          </span>
                        </div>

                        <div className="font-medium text-sm text-[#A9A9A9] flex justify-between items-center gap-2">
                          <span className="flex-1 min-w-0 break-words">
                            {item.eventname}
                          </span>
                          <span
                            className={`flex items-center gap-2.5 float-right ${matchStyles[item.matchtype]}`}
                          >
                            {item.matchtype}
                            <img
                              src={`/icons/Exchange/${item.categorytype}.svg`}
                              alt="icon"
                            />
                          </span>
                        </div>

                        {openRowIds.includes(item.id) && (
                          <div className="grid grid-rows-2 gap-2 border-t border-[#353535] mt-2 pt-2 font-medium text-sm text-[#A9A9A9]">
                            {item.status ===
                              TransactionStatusTypes.COMMISSION && (
                                <>
                                  <div>
                                    <span>{localization.betid}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {item.id}
                                      <img
                                        src={"/icons/ic-copy.svg"}
                                        alt="Copy"
                                        className="size-4 inline ml-2 cursor-pointer"
                                        onClick={() =>
                                          common.copyIdToClipboard(
                                            item.id,
                                            localization.betid_success_copied,
                                          )
                                        }
                                      />
                                    </span>
                                  </div>
                                  <div>
                                    <span>{localization.commission}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {item.commission.toFixed(2) + "\u20B9"}
                                    </span>
                                  </div>
                                </>
                              )}

                            {item.status !==
                              TransactionStatusTypes.COMMISSION && (
                                <>
                                  <div>
                                    <span>{localization.betid}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {item.id}
                                      <img
                                        src={"/icons/ic-copy.svg"}
                                        alt="Copy"
                                        className="size-4 inline ml-2 cursor-pointer"
                                        onClick={() =>
                                          common.copyIdToClipboard(
                                            item.id,
                                            localization.transid_success_copied,
                                          )
                                        }
                                      />
                                    </span>
                                  </div>

                                  <div>
                                    <span>{localization.event_date}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {formatUtcToLocalDateTime(
                                        item.transactiondate,
                                      )}
                                    </span>
                                  </div>

                                  <div>
                                    <span>{localization.stake}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {item.stake
                                        ? `${item.stake.toFixed(2)}\u20B9`
                                        : "-"}
                                    </span>
                                  </div>

                                  <div>
                                    <span>{localization.liability}</span>
                                    <span className="float-right text-[#E6E6E6]">
                                      {calculateLiability(item).toFixed(2) +
                                        "\u20B9"}
                                    </span>
                                  </div>
                                </>
                              )}
                          </div>
                        )}
                      </div>
                    </TransactionCard>
                  </div>
                );
            })}
        </div>
      </InfiniteScroll>
    </div>
  );
}
